import { create } from 'zustand';
import React from 'react';
import { PATHS, SCAN_PROCESS_STEPS, WORKFLOW_TYPES, MAIN_PROCESS_STEPS, WORKFLOW_CONFIG } from '../constants/flowConfig';
import { TOOLS_CONFIG } from '../constants/tools';

// Step configurations with labels and descriptions
export const STEP_CONFIGS = {
  [SCAN_PROCESS_STEPS.PREPARING_SCAN]: {
    label: 'Preparing Scan',
    description: 'Clean and prepare the scanned 3D model',
    tools: [TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.TRIM.id, TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.ERASE.id, TOOLS_CONFIG.SCAN_PROCESS.PREPARING_SCAN.FILL.id]
  },
  [SCAN_PROCESS_STEPS.AXES_ALIGNMENT]: {
    label: 'Axes & Alignment',
    description: 'Align the model axes for proper orientation',
    tools: [TOOLS_CONFIG.SCAN_PROCESS.AXES_ALIGNMENT.AXES.id, TOOLS_CONFIG.SCAN_PROCESS.AXES_ALIGNMENT.ALINGMENT.id]
  },
  [SCAN_PROCESS_STEPS.FINAL_DESIGN_PREVIEW]: {
    label: 'Final Design Preview',
    description: 'Review the prepared scan before main process',
    tools: []
  },
  [MAIN_PROCESS_STEPS.RECTIFICATION]: {
    label: 'Rectification',
    description: 'Apply rectification to the socket design',
    tools: [
      TOOLS_CONFIG.MAIN_PROCESS.RECTIFICATION.main_rectification_blocking_zone.id,
      TOOLS_CONFIG.MAIN_PROCESS.RECTIFICATION.main_rectification_measure_reduction.id,
      TOOLS_CONFIG.MAIN_PROCESS.RECTIFICATION.main_rectification_global_reduction.id,
      TOOLS_CONFIG.MAIN_PROCESS.RECTIFICATION.main_rectification_scoop_recharge.id,
      TOOLS_CONFIG.MAIN_PROCESS.RECTIFICATION.main_rectification_smoothning.id
    ]
  },
  [MAIN_PROCESS_STEPS.ELONGATION]: {
    label: 'Elongation',
    description: 'Extend the socket design as needed',
    tools: ['main_elongation_elongation']
  },
  [MAIN_PROCESS_STEPS.TRIMLINE]: {
    label: 'Trimline',
    description: 'Define trimlines for the socket',
    tools: ['main_trimline_trimline']
  },
  [MAIN_PROCESS_STEPS.FLARES]: {
    label: 'Flares',
    description: 'Add flares to the socket design',
    tools: [
      TOOLS_CONFIG.MAIN_PROCESS.FLARES.main_flares_flare.id,
      TOOLS_CONFIG.MAIN_PROCESS.FLARES.main_flares_smoothning.id
    ]
  },
  [MAIN_PROCESS_STEPS.THICKNESS]: {
    label: 'Thickness',
    description: 'Set wall thickness for the socket',
    tools: ['main_thickness_thickness']
  },
  [MAIN_PROCESS_STEPS.LOCKING_MECHANISM]: {
    label: 'Locking Mechanism',
    description: 'Add locking mechanism to the socket',
    tools: ['main_locking_lock']
  },
  [MAIN_PROCESS_STEPS.ALIGNMENT]: {
    label: 'Alignment',
    description: 'Final alignment adjustments',
    tools: []
  },
  [MAIN_PROCESS_STEPS.VALVE]: {
    label: 'Valve',
    description: 'Add valve to the socket',
    tools: ['main_valve_valve']
  },
  [MAIN_PROCESS_STEPS.PADS]: {
    label: 'Pads',
    description: 'Add comfort pads to the socket',
    tools: ['main_pads_pad']
  },
  [MAIN_PROCESS_STEPS.FINALIZATION]: {
    label: 'Finalization',
    description: 'Final review and completion',
    tools: ['main_finalization_review', 'main_finalization_export', 'main_finalization_save']
  },
  // Add config for the new tool
  main_rectification_smoothning: {
    label: 'Smoothning',
    description: 'Smooth the socket surface',
    icon: '/tool_icons/scan_preparing_eraser_smoothning.png',
    // Use the same tool card as scoop/recharge
  },
  main_flares_flare: {
    label: 'Flare',
    description: 'Configure flare segments',
    icon: '/tool_icons/main_flares_flares.png',
  },
  main_flares_smoothning: {
    label: 'Flares Smoothening',
    description: 'Smoothen flare deformations',
    icon: '/tool_icons/scan_preparing_eraser_smoothning.png',
  },
  main_trimline_trimline: {
    label: 'Trimline',
    description: 'Define trimlines for the socket',
    icon: '/tool_icons/main_trimline_trimline.png',
  },
  main_thickness_thickness: {
    label: 'Thickness',
    description: 'Set wall thickness for the socket',
    icon: '/tool_icons/main_thickness_thickness.png',
  }
};

const useWorkflowStore = create((set, get) => ({
  // ===== STATE =====
  // Current workflow and step
  currentWorkflow: WORKFLOW_TYPES.SCAN_PROCESS,
  currentStep: SCAN_PROCESS_STEPS.PREPARING_SCAN,
  
  // Patient data
  patientData: null,
  isLoadingPatientData: false,
  
  // Socket type selection modal
  showSocketTypeModal: false,
  selectedSocketType: 'mould',
  
  // Step completion tracking
  stepCompletion: {
    // Scan process steps
    [SCAN_PROCESS_STEPS.PREPARING_SCAN]: false,
    [SCAN_PROCESS_STEPS.AXES_ALIGNMENT]: false,
    [SCAN_PROCESS_STEPS.FINAL_DESIGN_PREVIEW]: false,
    
    // Main process steps
    [MAIN_PROCESS_STEPS.RECTIFICATION]: false,
    [MAIN_PROCESS_STEPS.ELONGATION]: false,
    [MAIN_PROCESS_STEPS.TRIMLINE]: false,
    [MAIN_PROCESS_STEPS.FLARES]: false,
    [MAIN_PROCESS_STEPS.THICKNESS]: false,
    [MAIN_PROCESS_STEPS.LOCKING_MECHANISM]: false,
    [MAIN_PROCESS_STEPS.ALIGNMENT]: false,
    [MAIN_PROCESS_STEPS.VALVE]: false,
    [MAIN_PROCESS_STEPS.PADS]: false,
    [MAIN_PROCESS_STEPS.FINALIZATION]: false,
  },

  // ===== ACTIONS =====
  actions: {
    // --- WORKFLOW NAVIGATION ---
    setCurrentWorkflow: (workflow) => {
      set({ currentWorkflow: workflow });
    },

    setCurrentStep: (step) => {
      set({ currentStep: step });
    },

    // Navigate to next step
    goToNextStep: () => {
      const { currentWorkflow, currentStep, stepCompletion } = get();
      
      if (currentWorkflow === WORKFLOW_TYPES.SCAN_PROCESS) {
        const scanSteps = Object.values(SCAN_PROCESS_STEPS);
        const currentIndex = scanSteps.indexOf(currentStep);
        
        if (currentIndex < scanSteps.length - 1) {
          const nextStep = scanSteps[currentIndex + 1];
          set({ currentStep: nextStep });
        } else {
          // Move to main process
          set({ 
            currentWorkflow: WORKFLOW_TYPES.MAIN_PROCESS,
            currentStep: MAIN_PROCESS_STEPS.RECTIFICATION
          });
        }
      } else if (currentWorkflow === WORKFLOW_TYPES.MAIN_PROCESS) {
        const mainSteps = Object.values(MAIN_PROCESS_STEPS);
        const currentIndex = mainSteps.indexOf(currentStep);
        
        if (currentIndex < mainSteps.length - 1) {
          const nextStep = mainSteps[currentIndex + 1];
          set({ currentStep: nextStep });
        }
      }
    },

    // Navigate to previous step
    goToPreviousStep: () => {
      set((state) => {
        const { currentWorkflow, currentStep } = state;
        const config = WORKFLOW_CONFIG[currentWorkflow];
    
        if (!config) return {}; // Safety check, do nothing if workflow is unknown
    
        const currentIndex = config.steps.indexOf(currentStep);
    
        if (currentIndex > 0) {
          // Case 1: Move to the previous step within the SAME workflow.
          const prevStep = config.steps[currentIndex - 1];
          return { currentStep: prevStep };
        } else if (config.previousWorkflow) {
          // Case 2: We're at the first step, so move to the PREVIOUS workflow.
          return {
            currentWorkflow: config.previousWorkflow.workflow,
            currentStep: config.previousWorkflow.step,
          };
        }
    
        // Case 3: At the very first step of the entire process. Do nothing.
        return {};
      });
    },

    // --- STEP COMPLETION ---
    setStepCompletion: (step, completed = true) => {
      set(state => ({
        stepCompletion: {
          ...state.stepCompletion,
          [step]: completed
        }
      }));
    },

    // Complete current step and move to next
    completeCurrentStep: () => {
      const { currentStep, currentWorkflow, actions } = get();
      
      // 1. Mark the current step as complete
      actions.setStepCompletion(currentStep, true);
    
      // 2. Check for and execute any special handlers for this step
      const handler = WORKFLOW_CONFIG[currentWorkflow]?.stepHandlers?.[currentStep]?.onComplete;
      if (handler) {
        const result = handler(set);
        // If the handler took over (e.g., to show a modal), stop here.
        if (result?.handled) return null;
      }
    
      // 3. Determine the next step using the config
      const config = WORKFLOW_CONFIG[currentWorkflow];
      const currentIndex = config.steps.indexOf(currentStep);
      let nextState = {};
    
      if (currentIndex < config.steps.length - 1) {
        // Case A: Next step is in the same workflow
        const nextStep = config.steps[currentIndex + 1];
        nextState = { currentStep: nextStep };
      } else if (config.nextWorkflow) {
        // Case B: Transition to the next workflow
        nextState = {
          currentWorkflow: config.nextWorkflow.workflow,
          currentStep: config.nextWorkflow.step,
        };
      } else {
        // Case C: End of all workflows
        console.log("All workflow steps completed.");
        return null; // Or return a path to a summary page
      }
    
      // 4. Update the store with the new state
      // set({currentStep: nextState?.currentStep});
    
      // 5. Return the correct path for the *new* step from your PATHS object
      return PATHS[nextState.currentStep]; //
    },

    // Socket type selection modal actions
    showSocketTypeSelectionModal: () => {
      set({ showSocketTypeModal: true });
    },

    hideSocketTypeSelectionModal: () => {
      set({ showSocketTypeModal: false });
    },

    setSelectedSocketType: (socketType) => {
      set({ selectedSocketType: socketType });
    },

    handleSocketTypeContinue: (socketType) => {
      const { actions } = get();
      set({ 
        selectedSocketType: socketType,
        showSocketTypeModal: false 
      });
      
      // Complete the FLARES step and navigate to THICKNESS step
      actions.setStepCompletion(MAIN_PROCESS_STEPS.FLARES, true);
      set({ currentStep: MAIN_PROCESS_STEPS.THICKNESS });
      
      // Return the route for navigation to thickness step using PATHS mapping
      return PATHS[MAIN_PROCESS_STEPS.THICKNESS];
    },

    // --- PATIENT DATA ---
    fetchPatientData: async () => {
      set({ isLoadingPatientData: true });
      try {
        // Simulate an API call with a 1-second delay
        await new Promise(res => setTimeout(res, 500));
        const mockApiResponse = {
          patientDetails: {
            "Name": 'John Doe',
            "Date of birth": '01/01/1999',
            "Gender": 'Male',
            // "Mobile No.": '00-00000000',
            // "Email": 'someoneddddddd@example.com',
            "Height (in cm)": 167,
            "Weight (in kg)": 70,
            "Shoe Size": 42,
          },
          keyMeasurements: {
            "Foot Type": 'Standard',
            "Amputation Side": 'Right',
            "Stump Type": 'Transtibial',
            "Locking Type": 'Bull Dog Lock',
            "Adapter Type": 'Pyramidal',
            "Flexion Angle (Deg)": 5,
            "Add/Abd Angle (Deg)": 2,
            "Arch Height (mm)": 30,
            "Heel Width (mm)": 70,
            "Ball Width (mm)": 85,
          },
          globalReduction: {
            "Global Value Reduction": "+1",
            "Patella (mm)": "+1",
            "Patella Tendon (mm)": "-8",
            "Crest of Tibia (mm)": "+2",
            "Lateral Shift Tibia (mm)": "+3",
            "Medial Shift Tibia (mm)": "-3",
            "Medial Supracondylar (mm)": "-3",
            "Distal Shift Tibia (mm)": "+6",
            "Hamstring Tendons (mm)": "+2"
          }
        };
        console.log('Fetched patient data:', mockApiResponse);
        set({
          patientData: mockApiResponse,
          isLoadingPatientData: false
        });
      } catch (error) {
        console.error('Error fetching patient data:', error);
        set({ isLoadingPatientData: false });
      }
    },

    // --- UTILITY FUNCTIONS ---
    getCurrentStepConfig: () => {
      const { currentStep } = get();
      return STEP_CONFIGS[currentStep] || {};
    },

    getCurrentWorkflowSteps: () => {
      const { currentWorkflow } = get();
      if (currentWorkflow === WORKFLOW_TYPES.SCAN_PROCESS) {
        return Object.values(SCAN_PROCESS_STEPS);
      } else {
        return Object.values(MAIN_PROCESS_STEPS);
      }
    },

    canNavigateToStep: (step) => {
      const { stepCompletion, currentWorkflow } = get();
      const currentSteps = currentWorkflow === WORKFLOW_TYPES.SCAN_PROCESS 
        ? Object.values(SCAN_PROCESS_STEPS)
        : Object.values(MAIN_PROCESS_STEPS);
      
      const stepIndex = currentSteps.indexOf(step);
      if (stepIndex === 0) return true; // First step is always accessible
      
      // Check if all previous steps are completed
      return currentSteps.slice(0, stepIndex).every(s => stepCompletion[s]);
    },

    isStepCompleted: (step) => {
      const { stepCompletion } = get();
      return stepCompletion[step] || false;
    },

    isCurrentStepCompleted: () => {
      const { currentStep, stepCompletion } = get();
      return stepCompletion[currentStep] || false;
    }
  },
}));

export const useWorkflowState = (selector, equalityFn) => useWorkflowStore(selector, equalityFn);
export const useWorkflowActions = () => useWorkflowStore((state) => state.actions);

export default useWorkflowStore;